import { motion } from 'motion/react';
import { twMerge } from 'tailwind-merge';

import { Icon, IconName } from '~/modules/elements/akui/icon';
import Logo from '~/modules/elements/logo';
import { HelpEntry } from '~/routes/keyboard-help/context';
import { ControlDescriptor, renderableControls } from '~/routes/keyboard-help/controls';
import { activateControl } from '~/routes/remote-mic/panels/microphone/remote-actions';
import RemoteControl from '~/routes/remote-mic/panels/microphone/remote-controls';
import { screenTransition } from '~/routes/remote-mic/panels/microphone/screen-transition';

type ButtonDescriptor = Extract<ControlDescriptor, { type: 'button' }>;

// The same tiers as the television: the one big action, the other two places worth going, then the
// room's setup as a list.
const SECONDARY = ['online', 'jukebox'];
const PREVIEW = ['online'];

const UTILITY_ICONS: Record<string, IconName> = {
  'select-input': 'ic:baseline-mic',
  singers: 'ic:baseline-person',
  'manage-songs': 'ic:baseline-library-music',
  settings: 'ic:baseline-settings',
};

// "none yet" is the whole reason the row says anything, so it is the one note in the accent.
const NOTHING_YET = 'none yet';

const isButton = (control: ControlDescriptor): control is ButtonDescriptor => control.type === 'button';

export default function MainMenuScreen({ keyboard }: { keyboard: HelpEntry }) {
  const controls = renderableControls(keyboard.controls ?? []);
  const primary = controls.find((control) => control.name === keyboard.primary);
  const secondary = controls.filter((control) => control !== primary && SECONDARY.includes(control.name));
  const utilities = controls.filter((control) => control !== primary && !secondary.includes(control));

  return (
    <motion.div
      data-test="remote-keyboard"
      data-mode="mirror"
      data-screen="main-menu"
      className="flex h-full min-h-0 w-full flex-col gap-3.5 overflow-y-auto overscroll-contain pt-0.5"
      {...screenTransition}>
      <div className="flex shrink-0 flex-col items-center gap-2 pt-1.5 pb-1">
        <div className="flex items-center gap-3">
          <img src="/logo.svg" alt="" className="size-10" />
          <div className="text-[2.25rem]">
            <Logo variant="phone" />
          </div>
        </div>
        <span className="text-[0.75rem] tracking-[0.08em] text-white/60 uppercase">Main menu · on the TV</span>
      </div>

      {primary && isButton(primary) && (
        <button
          type="button"
          onClick={() => activateControl(primary.name)}
          disabled={primary.disabled}
          data-test={`control-${primary.name}`}
          data-control-type="button"
          className="bg-active flex h-28 shrink-0 cursor-pointer items-center justify-center gap-3 rounded-[1.25rem] border-none text-[1.625rem] font-bold text-black shadow-[0_0_36px_rgba(255,165,0,0.28)] transition-transform duration-100 active:scale-[0.98] disabled:opacity-40">
          <Icon icon="ic:baseline-play-arrow" size={8.5} />
          {primary.label}
        </button>
      )}

      {secondary.length > 0 && (
        <div
          className="grid shrink-0 gap-2.5"
          style={{ gridTemplateColumns: `repeat(${secondary.length}, minmax(0, 1fr))` }}>
          {secondary.map((control) => (
            <button
              key={control.name}
              type="button"
              onClick={() => activateControl(control.name)}
              disabled={control.disabled}
              data-test={`control-${control.name}`}
              data-control-type={control.type}
              className="flex h-18 min-w-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-2xl border-none bg-black/50 px-2.5 text-[1.0625rem] font-bold text-white shadow-[inset_0_0_0_1px_rgba(255,165,0,0.45)] transition-transform duration-100 active:scale-[0.97] disabled:opacity-40">
              <span className="max-w-full truncate">{control.label}</span>
              {PREVIEW.includes(control.name) && (
                <span className="rounded-[0.3125rem] bg-white/14 px-1.5 py-px text-[0.625rem] font-bold tracking-[0.06em] uppercase">
                  Preview
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      <span aria-hidden className="my-0.5 h-px shrink-0 bg-white/10" />

      {utilities.length > 0 && (
        <div className="flex shrink-0 flex-col overflow-hidden rounded-[1.125rem] bg-black/55 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)] [&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-white/10">
          {utilities.map((control) =>
            isButton(control) ? (
              <UtilityRow key={control.name} control={control} />
            ) : (
              <RemoteControl key={control.name} control={control} onActivate={activateControl} inCard />
            ),
          )}
        </div>
      )}
    </motion.div>
  );
}

function UtilityRow({ control }: { control: ButtonDescriptor }) {
  const icon = UTILITY_ICONS[control.name];

  return (
    <button
      type="button"
      onClick={() => activateControl(control.name)}
      disabled={control.disabled}
      data-test={`control-${control.name}`}
      data-control-type="button"
      className="flex h-15.5 w-full cursor-pointer items-center gap-3.5 border-none bg-transparent px-4.5 text-left text-white transition-colors duration-150 active:bg-white/6 disabled:opacity-40">
      {icon && <Icon icon={icon} size={5.5} className="shrink-0 opacity-80" />}
      <span className="min-w-0 flex-1 truncate text-[1.0625rem]">{control.label}</span>
      {control.note && (
        <span
          data-test="utility-note"
          className={twMerge(
            'shrink-0 text-[0.875rem] tracking-[0.04em]',
            control.note === NOTHING_YET ? 'text-active' : 'text-white/70',
          )}>
          {control.note}
        </span>
      )}
      <Icon icon="ic:baseline-chevron-right" size={5.5} className="shrink-0 opacity-60" />
    </button>
  );
}
